import * as React from 'react';

import ResizeObserver from 'resize-observer-polyfill';

import {RoutedTab} from 'shells/tabs/routed-tab/routed-tab';
import {TabContainer} from 'shells/tabs/routed-tab/tab-container';

const PAGE_HEADER_HEIGHT = 32;
const MIN_REPORT_WINDOW_WIDTH = 960;
const UNREACHABLE_THRESHOLD = 100000;

type TabContent = {
    id: string; // clunky, but this is the `tab` match param (last part of url),
    body: React.ReactNode;
};

type Props = {
    selectedTab: string;
    location: Location;
    tabs: {
        id: string;
        label: string;
        pathname: string;
        exact: boolean;
    }[];
    tabContents: TabContent[];
    onStickyChange?: (contentTop?: number | null | undefined) => void;
};

type State = {
    isStickyHeaderActive: boolean;
};

/*
 * This container handles the creation of a tabbed interface at the bottom of
 * report pages, allowing us to show various details about the data being reported.
 *
 * It also detects the scroll position of the main report scroll position, and
 * lets children know when they might want to apply a sticky header style.
 */
export class ReportTabs extends React.Component<Props, State> {
    // @ts-expect-error
    resizeObserver: ResizeObserver;
    pageContentDiv: HTMLElement | null | undefined;
    pageContentTop: number;
    tabsRef: HTMLElement | null | undefined;

    constructor() {
        // @ts-expect-error
        super();
        this.state = {
            isStickyHeaderActive: false,
        };
        this.pageContentTop = 0;
    }

    componentDidMount() {
        // @ts-expect-error
        this.pageContentDiv = document.querySelector('#report-content-scroll-container');

        // Disable usual horizontal scrollbars
        if (this.pageContentDiv) {
            this.pageContentDiv.style.overflowX = 'hidden';
        }

        this.resizeObserver = new ResizeObserver((entries) => {
            for (const entry of entries) {
                if (entry.target === this.pageContentDiv) {
                    // Detect and react to changes in the offset of the scroll container
                    // (this happens during FOUC in development, for example)
                    const pageOffsetTop =
                        // @ts-expect-error
                        entry.target.offsetParent &&
                        // @ts-expect-error
                        entry.target.offsetParent instanceof HTMLElement &&
                        // @ts-expect-error
                        entry.target.offsetParent.offsetTop;
                    if (
                        typeof pageOffsetTop === 'number' &&
                        pageOffsetTop !== this.pageContentTop
                    ) {
                        this.pageContentTop = pageOffsetTop;
                    }
                }
            }
        });

        if (this.pageContentDiv) {
            this.resizeObserver.observe(this.pageContentDiv);
        }

        if (this.pageContentDiv) {
            this.pageContentDiv.addEventListener('scroll', this.handleScroll);

            this.pageContentTop = this.pageContentDiv.getBoundingClientRect().top;
        }
    }

    componentWillUnmount() {
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }

        if (this.pageContentDiv) {
            this.pageContentDiv.removeEventListener('scroll', this.handleScroll);
        }
    }

    componentDidUpdate(prevProps: Props, prevState: State) {
        if (
            prevState.isStickyHeaderActive !== this.state.isStickyHeaderActive &&
            this.props.onStickyChange
        ) {
            this.props.onStickyChange(this.state.isStickyHeaderActive ? this.pageContentTop : null);
        }
    }

    render() {
        // @ts-expect-error
        const isTabActive = (tab) => tab.id === this.props.selectedTab;

        return (
            <div id='report-tabs'>
                <TabContainer
                    getRef={(node) => {
                        this.tabsRef = node;
                    }}
                >
                    {this.props.tabs.map((tab) => {
                        return (
                            <RoutedTab
                                key={tab.id}
                                id={tab.id}
                                pathname={tab.pathname}
                                exact={tab.exact}
                                search={this.props.location.search}
                            >
                                {tab.label}
                            </RoutedTab>
                        );
                    })}
                </TabContainer>
                {this.props.tabContents.map((tabContent) => (
                    <div
                        key={tabContent.id}
                        style={{display: isTabActive(tabContent) ? 'block' : 'none'}}
                    >
                        {tabContent.body}
                    </div>
                ))}
            </div>
        );
    }

    handleScroll = () => {
        if (this.pageContentDiv) {
            const isStickyHeaderActive = this.state.isStickyHeaderActive;
            const isThresholdCrossed = this.pageContentDiv.scrollTop > this.getStickyThreshold();

            if (isThresholdCrossed && !isStickyHeaderActive) {
                requestAnimationFrame(() => this.setState({isStickyHeaderActive: true}));
            } else if (!isThresholdCrossed && isStickyHeaderActive) {
                requestAnimationFrame(() => this.setState({isStickyHeaderActive: false}));
            }
        }
    };

    getStickyThreshold = () => {
        // If our window is smaller than the minimum to start allowing left-right
        // scrolling, prevent our sticky header, as it's really annoying to
        // maintain a fixed-position element with left-right scroll.
        if (window.innerWidth < MIN_REPORT_WINDOW_WIDTH) {
            return UNREACHABLE_THRESHOLD;
        }

        return this.tabsRef ? this.tabsRef.offsetTop + PAGE_HEADER_HEIGHT : PAGE_HEADER_HEIGHT;
    };
}
